// Events definitions to be tracked
import {needsToBeNormalized} from '../../../events';

// Business mappers and builders
import {normalizedEcommerceEvent} from '../../../business/mappers/ecommerceEvents';
import {
  buildProducts, buildPromotions, buildProductTypeList,
  buildGA4Products, buildGA4Promotions,
} from './productsBuilder';

/**
 * UA Builder of ecommerce payload to send in the final datalayer object
 * @param {string} eventName Event name to recognize events vs ecommerce tracks
 * @param {object} originalPayload Payload sent from the app who calls track
 * @return {object} Ecommerce object to final dataLayer
 */
export const buildEcommercePayload = (eventName, originalPayload) => {
  let finalPayload = {};
  
  let promotionsToBuild = [];
  if (originalPayload.promotions) {
    promotionsToBuild = originalPayload.promotions;
  } else if (originalPayload.promotion) {
    promotionsToBuild = [originalPayload.promotion];
  }

  if (originalPayload.promotions && !originalPayload.promotion) {
    originalPayload.promotion = { 
      id: 'PLACEHOLDER', 
      name: 'PLACEHOLDER', 
      creative: 'PLACEHOLDER' 
    };
  }

  if (needsToBeNormalized(eventName)) {
    finalPayload = {
      ...(originalPayload.currencyCode ? {
        currencyCode: originalPayload.currencyCode,
      } : {}),
      [normalizedEcommerceEvent(eventName)]: {
        ...(originalPayload.actionField ? {
          actionField: originalPayload.actionField,
        } : {}),
        ...(originalPayload.products ? {
          products: originalPayload.products.map(buildProducts),
        } : {}),
        
        ...(promotionsToBuild.length > 0 ? {
          promotions: promotionsToBuild.map(buildPromotions),
        } : {}),
      },
    };
  } else {
    finalPayload = {
      currencyCode: originalPayload.currencyCode,
      impressions: originalPayload.impressions.map(buildProducts),
    };
  }

  return {
    ...(originalPayload.products ? {
      productList: buildProductTypeList(originalPayload.products),
    } : {}),
    ...(originalPayload.impressions ? {
      productList: buildProductTypeList(originalPayload.impressions),
    } : {}),
    ecommerce: {
      ...finalPayload,
    },
  };
};

/**
 * GA4 Builder of ecommerce payload to send in the final datalayer object
 * @param {string} eventName Event name to recognize events vs ecommerce tracks
 * @param {object} originalPayload Payload sent from the app who calls track
 * @return {object} Ecommerce object to final dataLayer
 */
export const buildGA4EcommercePayload = (eventName, originalPayload) => {
  let ga4Items = [];

  if (originalPayload.products) {
    ga4Items = originalPayload.products.map(buildGA4Products);
  } else if (originalPayload.impressions) {
    ga4Items = originalPayload.impressions.map(buildGA4Products);
  } else if (originalPayload.promotions) {
    ga4Items = originalPayload.promotions.map(buildGA4Promotions);
  } else if (originalPayload.promotion) {
    if (!originalPayload.promotions) {
      ga4Items = [originalPayload.promotion].map(buildGA4Promotions);
    }
  }

  const finalPayload = {
    /* transactional data */
    ...(originalPayload.actionField ? {
      ...(originalPayload.actionField.id ? {
        transaction_id: originalPayload.actionField.id,
      } : {}),
      ...(originalPayload.actionField.revenue ? {
        value: originalPayload.actionField.revenue,
      } : {}),
      ...(originalPayload.actionField.tax ? {
        tax: originalPayload.actionField.tax,
      } : {}),
      ...(originalPayload.actionField.shipping !== undefined ? {
        shipping: originalPayload.actionField.shipping,
      } : {}),
      ...(originalPayload.actionField.shippingTier ? {
        shipping_tier: originalPayload.actionField.shippingTier,
      } : {}),
      ...(originalPayload.actionField.coupon ? {
        coupon: originalPayload.actionField.coupon,
      } : {}),
    } : {}),
    /* transactional data */
    /* product data */
    // Usamos el array de items consolidado
    ...(ga4Items.length > 0 ? {
      items: ga4Items,
    } : {}),        
    /* product data */
    /* event specific data */
    ...(originalPayload.currencyCode ? {
      currency: originalPayload.currencyCode,
    } : {}),
    ...(originalPayload.paymentType ? {
      payment_type: originalPayload.paymentType,
    } : {}),
    ...(originalPayload.paymentInstallments ? {
      payment_installments: originalPayload.paymentInstallments,
    } : {}),
    /* event specific data */
  };

  return {
    ...(originalPayload.original_country ? {
      original_country: originalPayload.original_country,
    } : {}),
    ...(originalPayload.order ? {
      order: originalPayload.order,
    } : {}),
    ...(originalPayload.currencyCode ? {
      currency: originalPayload.currencyCode,
    } : {}),
    ...(originalPayload.payment_type ? {
      payment_type: originalPayload.payment_type,
    } : {}),
    ...(originalPayload.paymentInstallments ? {
      payment_installments: originalPayload.paymentInstallments,
    } : {}),
    ...(originalPayload.origin_country ? {
      origin_country: originalPayload.origin_country,
    } : {}),
    ...(originalPayload.origin_airport ? {
      origin_airport: originalPayload.origin_airport,
    } : {}),
    ...(originalPayload.origin_city ? {
      origin_city: originalPayload.origin_city,
    } : {}),
    ...(originalPayload.destination_country ? {
      destination_country: originalPayload.destination_country,
    } : {}),
    ...(originalPayload.destination_airport ? {
      destination_airport: originalPayload.destination_airport,
    } : {}),
    ...(originalPayload.destination_city ? {
      destination_city: originalPayload.destination_city,
    } : {}),
    ...(originalPayload.departure_date ? {
      departure_date: originalPayload.departure_date,
    } : {}),
    ...(originalPayload.arrival_date ? {
      arrival_date: originalPayload.arrival_date,
    } : {}),
    ...(originalPayload.travel_type ? {
      travel_type: originalPayload.travel_type,
    } : {}),
    ...(originalPayload.business_unit ? {
      business_unit: originalPayload.business_unit,
    } : {}),
    ...(originalPayload.adults ? {
      adults: originalPayload.adults,
    } : {}),
    ...(originalPayload.children ? {
      children: originalPayload.children,
    } : {}),
    ...(originalPayload.infants ? {
      infants: originalPayload.infants,
    } : {}),
    ...(originalPayload.products ? {
      product_list: buildProductTypeList(originalPayload.products),
    } : {}),
    ...(originalPayload.impressions ? {
      product_list: buildProductTypeList(originalPayload.impressions),
    } : {}),
    ecommerce: {
      ...finalPayload,
    },

  };
};
